import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { useLang, pick } from "@/context/LangContext";
import { Button } from "@/components/ui/button";
import InquiryForm from "@/components/site/InquiryForm";

function PageHeader({ eyebrow, title, subtitle }) {
  return (
    <section className="relative bg-sage-900 text-bone">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
        <div className="section-eyebrow text-ochre-400">{eyebrow}</div>
        <h1 className="mt-3 font-display text-4xl sm:text-5xl font-semibold tracking-tight max-w-3xl">{title}</h1>
        {subtitle && <p className="mt-4 text-bone/80 max-w-2xl">{subtitle}</p>}
      </div>
    </section>
  );
}

export function TreatmentsPage() {
  const { t, lang } = useLang();
  const [items, setItems] = useState([]);
  useEffect(() => { axios.get(`${API}/treatments`).then((r) => setItems(r.data)); }, []);
  return (
    <div data-testid="treatments-page">
      <PageHeader eyebrow={t("nav.treatments")} title={t("treatments.title")} subtitle={t("treatments.subtitle")} />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map((tr) => (
          <Link to={`/treatments/${tr.slug}`} key={tr.slug} className="group bg-white rounded-3xl border border-stone-200 overflow-hidden hover:-translate-y-1 hover:shadow-lg transition-all" data-testid={`treatment-item-${tr.slug}`}>
            <div className="aspect-[16/10] overflow-hidden"><img src={tr.image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>
            <div className="p-6">
              <div className="text-[10px] uppercase tracking-widest text-ochre-500 font-semibold">{tr.category}</div>
              <div className="font-display font-semibold text-lg text-ink mt-1">{pick(tr.name, lang)}</div>
              <p className="mt-2 text-sm text-stone-600 line-clamp-2">{pick(tr.short_desc, lang)}</p>
              <div className="mt-4 flex items-center justify-between text-xs text-stone-500">
                <span>{t("treatments.from")} <span className="text-ochre-500 font-semibold">${tr.price_from_usd?.toLocaleString()}</span></span>
                <span>{tr.duration_days}</span>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function TreatmentDetail() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const [tr, setTr] = useState(null);
  useEffect(() => { axios.get(`${API}/treatments/${slug}`).then((r) => setTr(r.data)).catch(() => setTr(false)); }, [slug]);
  if (tr === null) return <div className="py-32 text-center text-stone-500">Loading…</div>;
  if (tr === false) return <div className="py-32 text-center text-stone-500">Treatment not found.</div>;
  return (
    <div data-testid="treatment-detail">
      <section className="relative">
        <div className="absolute inset-0"><img src={tr.image} alt="" className="w-full h-full object-cover" /><div className="absolute inset-0 bg-gradient-to-r from-sage-900/90 to-sage-900/30" /></div>
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-24 text-bone">
          <div className="section-eyebrow text-ochre-400">{tr.category}</div>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl font-semibold max-w-3xl">{pick(tr.name, lang)}</h1>
          <p className="mt-4 text-bone/85 max-w-2xl">{pick(tr.long_desc, lang) || pick(tr.short_desc, lang)}</p>
        </div>
      </section>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white rounded-2xl border border-stone-200 p-6"><div className="text-xs uppercase tracking-widest text-stone-500">{t("treatments.from")}</div><div className="font-display text-2xl text-sage-900 mt-1">${tr.price_from_usd?.toLocaleString()}</div></div>
            <div className="bg-white rounded-2xl border border-stone-200 p-6"><div className="text-xs uppercase tracking-widest text-stone-500">{t("treatments.duration")}</div><div className="font-display text-2xl text-sage-900 mt-1">{tr.duration_days}</div></div>
          </div>
          <div className="bg-white rounded-3xl border border-stone-200 p-8">
            <h3 className="font-display text-2xl">{t("inquiry.title")}</h3>
            <p className="mt-2 text-stone-600 text-sm">{t("inquiry.subtitle")}</p>
            <div className="mt-6"><InquiryForm variant="plain" /></div>
          </div>
        </div>
        <aside className="bg-sage-900 text-bone rounded-3xl p-8 h-fit">
          <div className="section-eyebrow text-ochre-400">{t("why.title")}</div>
          <ul className="mt-4 space-y-3 text-sm text-bone/85">
            <li>✓ JCI & NABH accredited hospitals</li>
            <li>✓ Written cost estimate, no hidden fees</li>
            <li>✓ Visa · Airport · Translator · Stay</li>
            <li>✓ 24/7 patient concierge</li>
          </ul>
        </aside>
      </div>
    </div>
  );
}

export function HospitalsPage() {
  const { t, lang } = useLang();
  const [items, setItems] = useState([]);
  useEffect(() => { axios.get(`${API}/hospitals`).then((r) => setItems(r.data)); }, []);
  return (
    <div data-testid="hospitals-page">
      <PageHeader eyebrow={t("nav.hospitals")} title={t("hospitals.title")} subtitle={t("hospitals.subtitle")} />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map((h) => (
          <Link to={`/hospitals/${h.id}`} key={h.id} data-testid={`hospitals-list-${h.id}`} className="group bg-white rounded-3xl border border-stone-200 overflow-hidden hover:-translate-y-1 hover:shadow-lg transition-all">
            <div className="aspect-[16/10] overflow-hidden"><img src={h.image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>
            <div className="p-6">
              <div className="text-xs uppercase tracking-widest text-ochre-500 font-semibold">{h.city}</div>
              <div className="font-display font-semibold text-lg text-ink mt-1">{pick(h.name, lang)}</div>
              <p className="mt-2 text-sm text-stone-600 line-clamp-3">{pick(h.description, lang)}</p>
              <div className="mt-4 flex flex-wrap gap-1.5">
                {(h.accreditations || []).map((a) => <span key={a} className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-sage-100 text-sage-900 font-semibold">{a}</span>)}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function HospitalDetail() {
  const { hid } = useParams();
  const { t, lang } = useLang();
  const [h, setH] = useState(null);
  useEffect(() => { axios.get(`${API}/hospitals/${hid}`).then((r) => setH(r.data)).catch(() => setH(false)); }, [hid]);
  if (h === null) return <div className="py-32 text-center text-stone-500">Loading…</div>;
  if (h === false) return <div className="py-32 text-center text-stone-500">Hospital not found.</div>;
  return (
    <div data-testid="hospital-detail">
      <section className="relative">
        <div className="absolute inset-0"><img src={h.image} alt="" className="w-full h-full object-cover" /><div className="absolute inset-0 bg-gradient-to-r from-sage-900/90 to-sage-900/30" /></div>
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-24 text-bone">
          <div className="section-eyebrow text-ochre-400">{h.city}</div>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl font-semibold max-w-3xl">{pick(h.name, lang)}</h1>
          <p className="mt-4 text-bone/85 max-w-2xl">{pick(h.description, lang)}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            {(h.accreditations || []).map((a) => <span key={a} className="text-[10px] uppercase tracking-wider px-3 py-1.5 rounded-full bg-bone/10 border border-bone/20 font-semibold">{a}</span>)}
          </div>
        </div>
      </section>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white rounded-2xl border border-stone-200 p-6"><div className="text-xs uppercase tracking-widest text-stone-500">{t("hospitals.beds")}</div><div className="font-display text-2xl text-sage-900 mt-1">{h.beds}</div></div>
            <div className="bg-white rounded-2xl border border-stone-200 p-6"><div className="text-xs uppercase tracking-widest text-stone-500">{t("hospitals.established")}</div><div className="font-display text-2xl text-sage-900 mt-1">{h.established}</div></div>
          </div>
          <div className="bg-white rounded-3xl border border-stone-200 p-8">
            <h3 className="font-display text-xl mb-3">Specialties</h3>
            <div className="flex flex-wrap gap-2">
              {(h.specialties || []).map((s) => <span key={s} className="px-3 py-1.5 rounded-full bg-sage-100 text-sage-900 text-sm font-medium">{s}</span>)}
            </div>
          </div>
        </div>
        <aside className="bg-white border border-stone-200 rounded-3xl p-8 h-fit">
          <h3 className="font-display text-xl mb-1">{t("inquiry.title")}</h3>
          <p className="text-xs text-stone-500 mb-4">{t("inquiry.subtitle")}</p>
          <InquiryForm variant="plain" />
        </aside>
      </div>
    </div>
  );
}

export function DoctorsPage() {
  const { t, lang } = useLang();
  const [items, setItems] = useState([]);
  useEffect(() => { axios.get(`${API}/doctors`).then((r) => setItems(r.data)); }, []);
  return (
    <div data-testid="doctors-page">
      <PageHeader eyebrow={t("nav.doctors")} title={t("doctors.title")} subtitle={t("doctors.subtitle")} />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map((d) => (
          <div key={d.id} className="bg-white rounded-3xl border border-stone-200 overflow-hidden hover:-translate-y-1 hover:shadow-md transition-all" data-testid={`doctor-item-${d.id}`}>
            <div className="aspect-[4/3] overflow-hidden bg-stone-100"><img src={d.image} alt="" className="w-full h-full object-cover" /></div>
            <div className="p-6">
              <div className="font-display font-semibold text-lg text-ink">{pick(d.name, lang)}</div>
              <div className="text-sm text-ochre-600 mt-0.5">{pick(d.specialty, lang)}</div>
              <div className="text-xs text-stone-500 mt-2">{d.qualifications}</div>
              <div className="text-xs text-stone-500 mt-1">{d.hospital}</div>
              <div className="mt-3 text-xs text-sage-900 font-semibold">{d.experience_years}+ {t("doctors.years_exp")}</div>
              <p className="mt-3 text-sm text-stone-600 line-clamp-3">{pick(d.bio, lang)}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ServicesPage() {
  const { t } = useLang();
  const items = t("services.items");
  return (
    <div data-testid="services-page">
      <PageHeader eyebrow={t("nav.services")} title={t("services.title")} subtitle={t("why.subtitle")} />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map(([title, body], i) => (
          <div key={i} className="bg-white rounded-3xl border border-stone-200 p-7 hover:-translate-y-1 hover:shadow-md transition-all" data-testid={`service-item-${i}`}>
            <div className="h-10 w-10 rounded-2xl bg-ochre-500 text-white flex items-center justify-center font-display font-bold">{i + 1}</div>
            <div className="mt-4 font-display font-semibold text-lg text-ink">{title}</div>
            <p className="mt-2 text-sm text-stone-600">{body}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export function AboutPage() {
  const { t, lang } = useLang();
  const values = t("about.values");
  return (
    <div data-testid="about-page">
      <PageHeader eyebrow={t("nav.about")} title={t("about.title")} subtitle={t("about.lead")} />
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-16">
        <div className="prose max-w-none">
          <h2 className="font-display text-3xl">{t("about.mission_title")}</h2>
          <p className="text-stone-600 leading-relaxed mt-3">{t("about.mission_body")}</p>
          <h2 className="font-display text-3xl mt-12">{t("about.values_title")}</h2>
          <div className="grid md:grid-cols-3 gap-4 mt-4 not-prose">
            {values.map(([tt, bb], i) => (
              <div key={i} className="bg-white border border-stone-200 rounded-3xl p-6">
                <div className="font-display font-semibold text-lg text-ink">{tt}</div>
                <p className="mt-2 text-sm text-stone-600">{bb}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export function BlogPage() {
  const { t, lang } = useLang();
  const [posts, setPosts] = useState([]);
  useEffect(() => { axios.get(`${API}/blog`).then((r) => setPosts(r.data)); }, []);
  return (
    <div data-testid="blog-page">
      <PageHeader eyebrow={t("nav.blog")} title={t("blog.title")} subtitle={t("blog.subtitle")} />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {posts.map((p) => (
          <Link to={`/blog/${p.slug}`} key={p.slug} className="group bg-white rounded-3xl border border-stone-200 overflow-hidden hover:-translate-y-1 hover:shadow-lg transition-all" data-testid={`blog-item-${p.slug}`}>
            <div className="aspect-[16/10] overflow-hidden"><img src={p.image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>
            <div className="p-6">
              <div className="font-display font-semibold text-lg text-ink">{pick(p.title, lang)}</div>
              <p className="mt-2 text-sm text-stone-600 line-clamp-3">{pick(p.excerpt, lang)}</p>
              <div className="mt-4 text-xs text-ochre-500 font-semibold uppercase tracking-widest">{t("blog.read")}</div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function BlogDetail() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const [p, setP] = useState(null);
  useEffect(() => { axios.get(`${API}/blog/${slug}`).then((r) => setP(r.data)).catch(() => setP(false)); }, [slug]);
  if (p === null) return <div className="py-32 text-center text-stone-500">Loading…</div>;
  if (p === false) return <div className="py-32 text-center text-stone-500">Post not found.</div>;
  return (
    <div data-testid="blog-detail">
      <PageHeader eyebrow={t("nav.blog")} title={pick(p.title, lang)} subtitle={pick(p.excerpt, lang)} />
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-16">
        <img src={p.image} alt="" className="rounded-3xl w-full aspect-[16/9] object-cover" />
        <div className="mt-8 whitespace-pre-line leading-relaxed text-stone-700">{pick(p.content, lang)}</div>
      </div>
    </div>
  );
}

export function ContactPage() {
  const { t, lang } = useLang();
  const { CONTACT } = require("@/i18n");
  return (
    <div data-testid="contact-page">
      <PageHeader eyebrow={t("nav.contact")} title={t("contact.title")} subtitle={t("contact.subtitle")} />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 grid lg:grid-cols-2 gap-10">
        <div className="space-y-6">
          <div className="bg-white border border-stone-200 rounded-3xl p-8">
            <div className="section-eyebrow">{t("contact.email")}</div>
            <a href={`mailto:${CONTACT.email}`} className="mt-2 block font-display text-xl text-sage-900 hover:text-ochre-500">{CONTACT.email}</a>
          </div>
          <div className="bg-white border border-stone-200 rounded-3xl p-8">
            <div className="section-eyebrow">{t("contact.phone")}</div>
            <a href={CONTACT.whatsapp} target="_blank" rel="noreferrer" className="mt-2 block font-display text-xl text-sage-900 hover:text-ochre-500">{CONTACT.phone}</a>
          </div>
          <div className="bg-white border border-stone-200 rounded-3xl p-8">
            <div className="section-eyebrow">{t("contact.address")}</div>
            <div className="mt-2 font-display text-xl text-sage-900">{lang === "ar" ? CONTACT.city_ar : CONTACT.city_en}</div>
          </div>
          <a href={CONTACT.facebook} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-sage-900 text-bone px-5 py-3 text-sm font-medium hover:bg-sage-700" data-testid="contact-facebook">
            Facebook →
          </a>
        </div>
        <div className="bg-white border border-stone-200 rounded-3xl p-8">
          <h3 className="font-display text-2xl">{t("contact.form_title")}</h3>
          <p className="mt-2 text-stone-600 text-sm">{t("inquiry.subtitle")}</p>
          <div className="mt-6"><InquiryForm variant="plain" /></div>
        </div>
      </div>
    </div>
  );
}
